import { Input, Textarea, Label, Button } from '@creanizer/ui'
import { Plus, X } from 'lucide-react'
import { useState } from 'react'
import type { CharacterDevelopment } from '@creanizer/types'

interface DevelopmentEditorProps {
  development: CharacterDevelopment
  onChange: (development: CharacterDevelopment) => void
}

export function DevelopmentEditor({
  development,
  onChange
}: DevelopmentEditorProps) {
  const [newPotential, setNewPotential] = useState('')
  const [newConflict, setNewConflict] = useState('')
  const [newChallenge, setNewChallenge] = useState('')
  const [newArc, setNewArc] = useState('')

  const handleArrayAdd = (
    field: 'growthPotential' | 'internalConflicts' | 'externalChallenges' | 'arcSuggestions',
    value: string
  ) => {
    if (value) {
      onChange({
        ...development,
        [field]: [...development[field], value]
      })
      if (field === 'growthPotential') setNewPotential('')
      if (field === 'internalConflicts') setNewConflict('')
      if (field === 'externalChallenges') setNewChallenge('')
      if (field === 'arcSuggestions') setNewArc('')
    }
  }

  const handleArrayRemove = (
    field: 'growthPotential' | 'internalConflicts' | 'externalChallenges' | 'arcSuggestions',
    index: number
  ) => {
    onChange({
      ...development,
      [field]: development[field].filter((_, i) => i !== index)
    })
  }

  const handleArrayUpdate = (
    field: 'growthPotential' | 'internalConflicts' | 'externalChallenges' | 'arcSuggestions',
    index: number,
    value: string
  ) => {
    const updated = [...development[field]]
    updated[index] = value
    onChange({
      ...development,
      [field]: updated
    })
  }

  const renderArraySection = (
    title: string,
    field: 'growthPotential' | 'internalConflicts' | 'externalChallenges' | 'arcSuggestions',
    value: string,
    setValue: (v: string) => void,
    placeholder: string
  ) => (
    <div>
      <Label className="font-medium">{title}</Label>
      <div className="space-y-2 mt-2">
        {development[field].map((item, index) => (
          <div key={index} className="flex gap-2">
            <Input
              value={item}
              onChange={(e) => handleArrayUpdate(field, index, e.target.value)}
              className="flex-1"
            />
            <Button
              size="sm"
              variant="ghost"
              onClick={() => handleArrayRemove(field, index)}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        ))}
        <div className="flex gap-2">
          <Input
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder={placeholder}
            onKeyPress={(e) => e.key === 'Enter' && handleArrayAdd(field, value)}
            className="flex-1"
          />
          <Button
            size="sm"
            onClick={() => handleArrayAdd(field, value)}
          >
            <Plus className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  )

  return (
    <div className="space-y-4">
      <div>
        <Label htmlFor="currentState">現在の状態</Label>
        <Textarea
          id="currentState"
          value={development.currentState}
          onChange={(e) => onChange({ ...development, currentState: e.target.value })}
          placeholder="例: 自分の力に自信を持ち始めているが、まだ未熟"
          rows={3}
          className="mt-1"
        />
      </div>

      {renderArraySection(
        '成長の可能性',
        'growthPotential',
        newPotential,
        setNewPotential,
        '例: リーダーシップの向上'
      )}

      {renderArraySection(
        '内的葛藤',
        'internalConflicts',
        newConflict,
        setNewConflict,
        '例: 正義と復讐心の間で揺れている'
      )}

      {renderArraySection(
        '外的課題',
        'externalChallenges',
        newChallenge,
        setNewChallenge,
        '例: 宿敵との対決'
      )}

      {renderArraySection(
        '物語の展開案',
        'arcSuggestions',
        newArc,
        setNewArc,
        '例: 仲間の裏切りを乗り越え、真のリーダーとなる'
      )}
    </div>
  )
}